import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { UrbanTrackDefinition, BuildingData, NeonSignData, ObstacleData } from '../game/tracks/UrbanTrackCatalog';
import { VegetationSystem } from '../game/environment/VegetationSystem';

/**
 * NeonEnvironmentRenderer - Rendet NFS Underground ähnliche urbane Umgebungen
 * mit Gebäuden, Neon-Lichtern, Straßendekor und realistischer Atmosphäre
 */
export class NeonEnvironmentRenderer {
  private environmentGroup = new THREE.Group();
  private neonLights: THREE.PointLight[] = [];
  private animatedNeons: Array<{ mesh: THREE.Mesh; originalColor: THREE.Color; intensity: number }> = [];
  private atmosphereLights: THREE.Light[] = [];
  private vegetationSystem: VegetationSystem;
  private gltfLoader = new GLTFLoader();
  private loadedEnvironmentMap: THREE.Group | null = null;
  private environmentHeightMeshes: THREE.Mesh[] = [];
  private environmentSolidMeshes: THREE.Mesh[] = [];
  private environmentCollisionBoxes: Array<{ mesh: THREE.Mesh; box: THREE.Box3; center: THREE.Vector3; radius: number; size: THREE.Vector3 }> = [];
  private heightRaycaster = new THREE.Raycaster();
  private collisionRaycaster = new THREE.Raycaster();

  constructor(private scene: THREE.Scene) {
    this.vegetationSystem = new VegetationSystem();
    this.scene.add(this.vegetationSystem.getGroup());
  }

  async renderEnvironment(track: UrbanTrackDefinition): Promise<void> {
    this.environmentGroup.clear();
    this.neonLights = [];
    this.animatedNeons = [];
    this.vegetationSystem.clear();

    for (const light of this.atmosphereLights) {
      this.scene.remove(light);
    }
    this.atmosphereLights = [];

    // Entferne alte Environment-Map wenn vorhanden
    if (this.loadedEnvironmentMap) {
      this.scene.remove(this.loadedEnvironmentMap);
      this.loadedEnvironmentMap = null;
    }
    this.environmentHeightMeshes = [];
    this.environmentSolidMeshes = [];
    this.environmentCollisionBoxes = [];

    // Lade externe GLB-Map wenn verfügbar
    if (track.environmentMapPath) {
      await this.loadEnvironmentMap(track.environmentMapPath, track.environmentMapScale || 1.0);
    }

    // 1. Atmosphäre & Himmel
    this.setupAtmosphere(track.environment);

    // 2. Straße & Checkpoints (immer rendern, damit Strecke befahrbar bleibt)
    this.renderTrack(track);

    // 4. Neon-Schilder & Lichter
    this.renderNeonSigns(track);

    // 5. Straßen-Dekoationen
    this.renderStreetDecorations(track);

    // 6. Dynamische Lichter
    this.setupDynamicLighting(track.environment);
    
    // 7. Vegetation für Race-Modus
    const trackCenter = track.spawnPoint.clone();
    if (track.environmentMapPath) {
      this.vegetationSystem.spawnVegetationArea(trackCenter, 120, 12);
    } else {
      this.vegetationSystem.spawnVegetationArea(trackCenter, 350, 45);
    }
    
    // 8. Schilder entlang Strecke
    const signPositions = [];
    for (let i = 0; i < track.checkpoints.length; i++) {
      const cp = track.checkpoints[i];
      const signTypes: Array<'speed' | 'stop' | 'yield' | 'parking'> = ['speed', 'yield'];
      signPositions.push({
        pos: new THREE.Vector3(cp.position.x + 20, 0, cp.position.z),
        type: signTypes[i % signTypes.length] as 'speed' | 'stop' | 'yield' | 'parking'
      });
    }
    this.vegetationSystem.spawnTrafficSigns(signPositions);

    if (track.raceType === 'circuit') {
      const endPosition = this.computeTrackEndPosition(track);
      this.addClosingSegment(track.spawnPoint.clone(), endPosition, track.segments[0]?.width ?? 12);
    }

    this.scene.add(this.environmentGroup);
  }

  private computeTrackEndPosition(track: UrbanTrackDefinition): THREE.Vector3 {
    let position = track.spawnPoint.clone();
    let yaw = 0;

    for (const seg of track.segments) {
      const dir = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      position.addScaledVector(dir, seg.length);

      if (seg.type === 'left_curve') yaw -= (seg.length / Math.max(25, seg.radius ?? 55)) * 0.9;
      if (seg.type === 'right_curve') yaw += (seg.length / Math.max(25, seg.radius ?? 55)) * 0.9;
      if (seg.type === 'hairpin') yaw += (Math.PI * 0.85) * (Math.random() < 0.5 ? 1 : -1);
      if (seg.type === 'chicane') yaw += (Math.random() < 0.5 ? 1 : -1) * 0.55;
    }

    return position;
  }

  /**
   * Lädt externe GLB-Map als Umgebung
   */
  private async loadEnvironmentMap(path: string, scale: number): Promise<void> {
    try {
      console.log(`Lade Environment-Map: ${path}`);
      const gltf = await this.gltfLoader.loadAsync(path);
      const mapGroup = gltf.scene;
      const mapMeshes: THREE.Mesh[] = [];
      
      mapGroup.scale.setScalar(scale);
      mapGroup.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.castShadow = false;
          mesh.receiveShadow = false;
          mesh.frustumCulled = true;
          mapMeshes.push(mesh);
        }
      });
      
      this.loadedEnvironmentMap = mapGroup;
      this.scene.add(mapGroup);
      this.loadedEnvironmentMap.updateMatrixWorld(true);
      this.environmentHeightMeshes = [];
      this.environmentSolidMeshes = [];
      this.environmentCollisionBoxes = mapMeshes
        .map((mesh) => {
          const box = new THREE.Box3().setFromObject(mesh);
          const center = new THREE.Vector3();
          box.getCenter(center);
          const size = new THREE.Vector3();
          box.getSize(size);
          const radius = Math.max(size.x, size.z) * 0.5;
          return { mesh, box, center, radius, size };
        })
        .filter((entry) => entry.size.x > 0.2 && entry.size.z > 0.2 && entry.size.y > 0.05);

      for (const entry of this.environmentCollisionBoxes) {
        const meshTag = this.getMeshTag(entry.mesh);
        const isRoadLike = this.isRoadLikeMesh(meshTag);
        const isSolidLike = this.isSolidLikeMesh(meshTag);

        const isHugeBasePlane = entry.size.x > 450 && entry.size.z > 450 && entry.size.y < 8;
        const isLikelyGround = entry.size.y < 2.2 || isRoadLike || isHugeBasePlane;
        const isLikelySolid =
          !isRoadLike &&
          (isSolidLike || (entry.size.y >= 2.2 && entry.size.x >= 0.8 && entry.size.z >= 0.8));

        if (isLikelyGround) {
          this.environmentHeightMeshes.push(entry.mesh);
        }
        if (isLikelySolid) {
          this.environmentSolidMeshes.push(entry.mesh);
        }
      }

      if (this.environmentHeightMeshes.length === 0) {
        this.environmentHeightMeshes = mapMeshes;
      }

      this.environmentCollisionBoxes = this.environmentCollisionBoxes.filter((entry) =>
        this.environmentSolidMeshes.includes(entry.mesh)
      );
      console.log(`Environment-Map erfolgreich geladen: ${path}`);
    } catch (error) {
      console.warn(`Fehler beim Laden der Environment-Map ${path}:`, error);
    }
  }

  private getMeshTag(mesh: THREE.Object3D): string {
    const names: string[] = [];
    let current: THREE.Object3D | null = mesh;
    let depth = 0;

    while (current && depth < 5) {
      if (current.name) names.push(current.name.toLowerCase());
      current = current.parent;
      depth += 1;
    }

    return names.join(' ');
  }

  private isRoadLikeMesh(tag: string): boolean {
    if (!tag) return false;
    return /(road|street|asphalt|ground|terrain|floor|pavement|lane|walk|sidewalk|base|tile|plane)/.test(tag);
  }

  private isSolidLikeMesh(tag: string): boolean {
    if (!tag) return false;
    return /(build|house|wall|facade|tower|block|fence|tree|pole|barrier|rail|rock|debris|shop|garage|bridge|prop)/.test(tag);
  }

  hasEnvironmentMap(): boolean {
    return Boolean(this.loadedEnvironmentMap);
  }

  getEnvironmentHeightAt(x: number, z: number): number | null {
    if (!this.loadedEnvironmentMap || this.environmentHeightMeshes.length === 0) return null;
    this.heightRaycaster.set(new THREE.Vector3(x, 3000, z), new THREE.Vector3(0, -1, 0));
    this.heightRaycaster.far = 6000;
    const hits = this.heightRaycaster.intersectObjects(this.environmentHeightMeshes, true);
    if (hits.length === 0) return null;
    return hits[0].point.y;
  }

  resolveEnvironmentCollision(previousPosition: THREE.Vector3, position: THREE.Vector3, radius: number): THREE.Vector3 | null {
    if (!this.loadedEnvironmentMap || this.environmentCollisionBoxes.length === 0) return null;

    const movement = new THREE.Vector3().subVectors(position, previousPosition).setY(0);
    const movementLen = movement.length();
    const movementDir = movementLen > 1e-4 ? movement.clone().multiplyScalar(1 / movementLen) : new THREE.Vector3(0, 0, 1);
    const maxRange = radius + movementLen + 8;

    const nearby = this.environmentCollisionBoxes
      .filter((entry) => {
        const dx = position.x - entry.center.x;
        const dz = position.z - entry.center.z;
        const distSq = dx * dx + dz * dz;
        const r = entry.radius + maxRange;
        return distSq <= r * r;
      })
      .map((entry) => entry.mesh);

    if (nearby.length === 0) return null;

    let bestPush: THREE.Vector3 | null = null;
    let bestPenetration = 0;
    const skin = 0.3;
    const yOffsets = [0.35, 0.9];

    for (const yOffset of yOffsets) {
      const origin = new THREE.Vector3(previousPosition.x, position.y + yOffset, previousPosition.z);
      this.collisionRaycaster.set(origin, movementDir);
      this.collisionRaycaster.far = Math.max(1.2, movementLen + radius + skin);

      const hit = this.collisionRaycaster.intersectObjects(nearby, true)[0];
      if (hit) {
        const penetration = this.collisionRaycaster.far - hit.distance;
        if (penetration > bestPenetration) {
          const pushDir = new THREE.Vector3(position.x - hit.point.x, 0, position.z - hit.point.z);
          if (pushDir.lengthSq() < 1e-6) {
            pushDir.copy(movementDir).multiplyScalar(-1);
          }
          pushDir.normalize();
          bestPenetration = penetration;
          bestPush = pushDir.multiplyScalar(Math.min(3, penetration));
        }
      }

      const radialDirections = [
        new THREE.Vector3(1, 0, 0),
        new THREE.Vector3(-1, 0, 0),
        new THREE.Vector3(0, 0, 1),
        new THREE.Vector3(0, 0, -1),
        new THREE.Vector3(1, 0, 1).normalize(),
        new THREE.Vector3(-1, 0, 1).normalize(),
        new THREE.Vector3(1, 0, -1).normalize(),
        new THREE.Vector3(-1, 0, -1).normalize()
      ];

      const radialOrigin = new THREE.Vector3(position.x, position.y + yOffset, position.z);
      for (const dir of radialDirections) {
        this.collisionRaycaster.set(radialOrigin, dir);
        this.collisionRaycaster.far = radius + skin;
        const sideHit = this.collisionRaycaster.intersectObjects(nearby, true)[0];
        if (!sideHit) continue;

        const penetration = (radius + skin) - sideHit.distance;
        if (penetration <= bestPenetration) continue;

        bestPenetration = penetration;
        bestPush = dir.clone().multiplyScalar(-Math.min(2.5, penetration + 0.15));
      }
    }

    return bestPush;
  }

  private setupAtmosphere(env: any): void {
    // Himmel-Dome
    const skyGeometry = new THREE.SphereGeometry(1000, 32, 32);
    const skyMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(env.skyColor),
      side: THREE.BackSide
    });
    const sky = new THREE.Mesh(skyGeometry, skyMaterial);
    this.environmentGroup.add(sky);

    // Fog bei Bedarf
    if (env.fogDensity) {
      this.scene.fog = new THREE.Fog(
        new THREE.Color(env.fogColor || '#999999'),
        500,
        1500
      );
    }

    // Ambientes Licht
    const ambientColor = env.timeOfDay === 'night'
      ? new THREE.Color(0x2244ff) // Kühler Blue-Ton nachts
      : new THREE.Color(0xffffff);

    const ambientLight = new THREE.AmbientLight(ambientColor, env.ambientLight);
    this.scene.add(ambientLight);
    this.atmosphereLights.push(ambientLight);

    // Zusätzliches Richtungslicht (Moon/Sun)
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight.position.set(200, 100, 200);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(2048, 2048);
    this.scene.add(dirLight);
    this.atmosphereLights.push(dirLight);
  }

  private renderTrack(track: UrbanTrackDefinition): void {
    let position = track.spawnPoint.clone();
    let yaw = 0; // heading in rad

    const roadMat = new THREE.MeshStandardMaterial({
      color: 0x14161b,
      metalness: 0.15,
      roughness: 0.78,
      side: THREE.DoubleSide
    });

    for (const seg of track.segments) {
      // Segment-Richtung
      const dir = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));

      // Segment-Mitte
      const center = position.clone().addScaledVector(dir, seg.length * 0.5);

      // Road plane
      const roadGeometry = new THREE.PlaneGeometry(seg.width, seg.length);
      const road = new THREE.Mesh(roadGeometry, roadMat);
      road.rotation.x = -Math.PI / 2;
      road.rotation.z = 0;
      road.rotation.y = yaw;
      road.position.copy(center);
      road.receiveShadow = true;
      this.environmentGroup.add(road);

      // Markings
      this.addRoadMarkings(center, seg.width, seg.length, yaw);

      // Edges
      this.addRoadEdgesCurved(center, seg, yaw);

      // Special: tunnel walls
      if (seg.type === 'tunnel') {
        this.addTunnelWallsCurved(center, seg, yaw);
      }

      // Special: jump elevation (visual only)
      if (seg.type === 'jump' && seg.elevation) {
        road.position.y += Math.max(0.05, seg.elevation * 0.08);
      }

      // Apply per-segment environment decor (buildings/neons/obstacles)
      if (seg.surroundings) {
        // Shift surroundings around segment center
        const left = new THREE.Vector3(-dir.z, 0, dir.x);
        const right = left.clone().multiplyScalar(-1);

        const baseL = center.clone().addScaledVector(left, seg.width * 0.75 + 6);
        const baseR = center.clone().addScaledVector(right, seg.width * 0.75 + 6);

        seg.surroundings.buildings?.forEach((b) => {
          // slight random spread around L/R
          const side = Math.random() < 0.5 ? baseL : baseR;
          const jitter = new THREE.Vector3((Math.random()-0.5)*8, 0, (Math.random()-0.5)*8);
          const pos = side.clone().add(jitter);
          this.renderBuilding({ ...b, position: [pos.x, pos.y, pos.z] });
        });

        seg.surroundings.neonSigns?.forEach((n) => {
          const side = Math.random() < 0.5 ? baseL : baseR;
          const jitter = new THREE.Vector3((Math.random()-0.5)*6, 0, (Math.random()-0.5)*6);
          const pos = side.clone().add(jitter);
          this.renderNeonSign({ ...n, position: [pos.x, pos.y + 2.5, pos.z] });
        });

        seg.surroundings.obstacles?.forEach((o) => {
          const pos = center.clone().addScaledVector(dir, (Math.random()-0.5)*seg.length*0.6);
          const off = (Math.random()<0.5?-1:1) * (seg.width*0.3);
          pos.add(new THREE.Vector3(-dir.z,0,dir.x).multiplyScalar(off));
          this.renderObstacle({ ...o, position: [pos.x, 0.05, pos.z] });
        });
      }

      // Update yaw by segment type
      if (seg.type === 'left_curve') yaw -= (seg.length / Math.max(25, seg.radius ?? 55)) * 0.9;
      if (seg.type === 'right_curve') yaw += (seg.length / Math.max(25, seg.radius ?? 55)) * 0.9;
      if (seg.type === 'hairpin') yaw += (Math.PI * 0.85) * (Math.random() < 0.5 ? 1 : -1);
      if (seg.type === 'chicane') yaw += (Math.random() < 0.5 ? 1 : -1) * 0.55;

      // advance to next position
      position.addScaledVector(dir, seg.length);
    }

    if (track.raceType === 'circuit') {
      this.addClosingSegment(track.spawnPoint.clone(), position.clone(), track.segments[0]?.width ?? 12);
    }
  }

  private addClosingSegment(start: THREE.Vector3, end: THREE.Vector3, width: number): void {
    const dir = new THREE.Vector3().subVectors(start, end);
    const length = dir.length();
    if (length < 1) return;
    dir.normalize();

    const center = end.clone().addScaledVector(dir, length * 0.5);
    const yaw = Math.atan2(dir.x, dir.z);

    const roadMat = new THREE.MeshStandardMaterial({
      color: 0x14161b,
      metalness: 0.15,
      roughness: 0.78,
      side: THREE.DoubleSide
    });

    const roadGeometry = new THREE.PlaneGeometry(width, length);
    const road = new THREE.Mesh(roadGeometry, roadMat);
    road.rotation.x = -Math.PI / 2;
    road.rotation.y = yaw;
    road.position.copy(center);
    road.receiveShadow = true;
    this.environmentGroup.add(road);

    this.addRoadMarkings(center, width, length, yaw);
    this.addRoadEdgesCurved(center, { length, width }, yaw);
  }

  private addRoadMarkings(position: THREE.Vector3, width: number, length: number, yaw: number): void {
    const markMaterial = new THREE.MeshStandardMaterial({
      color: 0xffff66,
      emissive: 0xffff66,
      emissiveIntensity: 0.22,
      metalness: 0,
      roughness: 0.85
    });

    const markSpacing = 6;
    const dir = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    const right = new THREE.Vector3(-dir.z, 0, dir.x);

    for (let i = -length / 2; i < length / 2; i += markSpacing * 2) {
      const markGeometry = new THREE.PlaneGeometry(0.42, markSpacing);
      const mark = new THREE.Mesh(markGeometry, markMaterial);
      mark.rotation.x = -Math.PI / 2;
      mark.rotation.y = yaw;

      mark.position.copy(position)
        .addScaledVector(dir, i)
        .addScaledVector(right, 0);

      mark.position.y = 0.012;
      this.environmentGroup.add(mark);
    }
  }

  private addRoadEdgesCurved(center: THREE.Vector3, segment: any, yaw: number): void {
    const dir = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    const right = new THREE.Vector3(-dir.z, 0, dir.x);

    const edgeMat = new THREE.MeshStandardMaterial({ color: 0x0e1118, roughness: 0.95, metalness: 0.02 });
    const edgeGeo = new THREE.PlaneGeometry(10, segment.length);

    const leftEdge = new THREE.Mesh(edgeGeo, edgeMat);
    leftEdge.rotation.x = -Math.PI / 2;
    leftEdge.rotation.y = yaw;
    leftEdge.position.copy(center).addScaledVector(right, segment.width * 0.5 + 5);
    leftEdge.position.y = 0.002;
    this.environmentGroup.add(leftEdge);

    const rightEdge = new THREE.Mesh(edgeGeo, edgeMat);
    rightEdge.rotation.x = -Math.PI / 2;
    rightEdge.rotation.y = yaw;
    rightEdge.position.copy(center).addScaledVector(right, -(segment.width * 0.5 + 5));
    rightEdge.position.y = 0.002;
    this.environmentGroup.add(rightEdge);
  }

  private addTunnelWallsCurved(center: THREE.Vector3, segment: any, yaw: number): void {
    const dir = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    const right = new THREE.Vector3(-dir.z, 0, dir.x);

    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x151824,
      roughness: 0.65,
      metalness: 0.08,
      emissive: 0x0b0f1b,
      emissiveIntensity: 0.35
    });

    const wallGeo = new THREE.BoxGeometry(0.6, 4.2, segment.length);
    const leftWall = new THREE.Mesh(wallGeo, wallMat);
    leftWall.rotation.y = yaw;
    leftWall.position.copy(center).addScaledVector(right, segment.width * 0.5 + 0.8);
    leftWall.position.y = 2.0;
    leftWall.castShadow = true;
    this.environmentGroup.add(leftWall);

    const rightWall = new THREE.Mesh(wallGeo, wallMat);
    rightWall.rotation.y = yaw;
    rightWall.position.copy(center).addScaledVector(right, -(segment.width * 0.5 + 0.8));
    rightWall.position.y = 2.0;
    rightWall.castShadow = true;
    this.environmentGroup.add(rightWall);
  }

  private renderBuilding(building: BuildingData): void {
    // Hauptstruktur
    const geometry = new THREE.BoxGeometry(building.width, building.height, building.depth);
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(building.color),
      metalness: 0.3,
      roughness: 0.7,
      side: THREE.DoubleSide
    });
    const buildingMesh = new THREE.Mesh(geometry, material);
    buildingMesh.position.set(...building.position);
    buildingMesh.castShadow = true;
    buildingMesh.receiveShadow = true;
    this.environmentGroup.add(buildingMesh);

    // Fenster
    if (building.windows) {
      this.addBuildingWindows(building);
    }
  }

  private addBuildingWindows(building: BuildingData): void {
    const windowSize = 1.2;
    const windowSpacing = 2;
    const windowMaterial = new THREE.MeshStandardMaterial({
      color: building.lit ? 0xffff88 : 0x222244,
      emissive: building.lit ? 0xffff44 : 0x000000,
      emissiveIntensity: building.lit ? 0.5 : 0,
      metalness: 0.8,
      roughness: 0.1,
      transparent: true,
      opacity: building.lit ? 0.9 : 0.4
    });

    const floorsCount = Math.floor(building.height / windowSpacing);
    const columnsCount = Math.floor(building.width / windowSpacing);

    for (let floor = 0; floor < floorsCount; floor++) {
      for (let col = 0; col < columnsCount; col++) {
        const windowGeometry = new THREE.PlaneGeometry(windowSize, windowSize);
        const window = new THREE.Mesh(windowGeometry, windowMaterial);

        window.position.set(
          building.position[0] - building.width / 2 + col * windowSpacing + windowSize,
          building.position[1] + floor * windowSpacing + windowSize,
          building.position[2] + building.depth / 2 + 0.1
        );

        this.environmentGroup.add(window);
      }
    }
  }

  private renderNeonSign(sign: NeonSignData): void {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Hintergrund (schwarz mit Glow)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 512, 256);

    // Text (Neon-Farbe)
    ctx.fillStyle = sign.color;
    ctx.font = 'bold 120px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(sign.text, 256, 128);

    // Glow-Effekt
    ctx.strokeStyle = sign.color;
    ctx.lineWidth = 8;
    ctx.globalAlpha = 0.3;
    ctx.strokeText(sign.text, 256, 128);
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    const signWidth = Math.max(4, sign.text.length * 1.6);
    const signHeight = 2.2;
    const geometry = new THREE.PlaneGeometry(signWidth, signHeight);
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      emissive: new THREE.Color(sign.color),
      emissiveMap: texture,
      emissiveIntensity: sign.intensity,
      metalness: 0,
      roughness: 0,
      transparent: true
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...sign.position);
    this.environmentGroup.add(mesh);

    // Neon-Licht hinzufügen
    const light = new THREE.PointLight(new THREE.Color(sign.color), sign.intensity * 2, 50);
    light.position.set(...sign.position);
    light.position.z += 5;
    this.environmentGroup.add(light);
    this.neonLights.push(light);

    // Zum Animieren speichern
    this.animatedNeons.push({
      mesh: mesh,
      originalColor: new THREE.Color(sign.color),
      intensity: sign.intensity
    });
  }

  private renderStreetDecorations(track: UrbanTrackDefinition): void {
    // Straßenlaternen
    if (track.environment.timeOfDay === 'night') {
      this.addStreetLights(track);
    }
  }

  private addStreetLights(track: UrbanTrackDefinition): void {
    const lampColor = 0xffff88;
    const lampSpacing = 30;

    track.segments.forEach((segment) => {
      const lampCount = Math.floor(segment.length / lampSpacing);

      for (let i = 0; i < lampCount; i++) {
        const position = new THREE.Vector3(
          -segment.width / 2 - 15,
          12,
          i * lampSpacing
        );

        // Lampen-Pole (grau/dunkelbraun)
        const poleGeometry = new THREE.CylinderGeometry(0.5, 0.5, 12, 8);
        const poleMaterial = new THREE.MeshStandardMaterial({
          color: 0x333333,
          metalness: 0.4,
          roughness: 0.6
        });
        const pole = new THREE.Mesh(poleGeometry, poleMaterial);
        pole.position.copy(position);
        pole.position.y = 6;
        pole.castShadow = true;
        this.environmentGroup.add(pole);

        // Lampen-Kopf (Lampe selbst)
        const lampGeometry = new THREE.CylinderGeometry(1.5, 1.5, 1, 16);
        const lampMaterial = new THREE.MeshStandardMaterial({
          color: lampColor,
          emissive: lampColor,
          emissiveIntensity: 0.4,
          metalness: 0.7,
          roughness: 0.3
        });
        const lamp = new THREE.Mesh(lampGeometry, lampMaterial);
        lamp.position.copy(position);
        lamp.castShadow = true;
        this.environmentGroup.add(lamp);

        // Licht-Quelle
        const pointLight = new THREE.PointLight(lampColor, 1.5, 80);
        pointLight.position.copy(position);
        pointLight.castShadow = true;
        this.environmentGroup.add(pointLight);
        this.neonLights.push(pointLight);
      }
    });
  }

  private renderNeonSigns(track: UrbanTrackDefinition): void {
    track.segments.forEach((segment) => {
      if (segment.surroundings?.neonSigns) {
        segment.surroundings.neonSigns.forEach((sign) => {
          this.renderNeonSign(sign);
        });
      }
    });
  }

  private renderObstacle(obstacle: ObstacleData): void {
    let geometry: THREE.BufferGeometry;
    let color = new THREE.Color('#FF6600');

    switch (obstacle.type) {
      case 'cone':
        geometry = new THREE.ConeGeometry(0.5, 2, 16);
        break;
      case 'barrier':
        geometry = new THREE.BoxGeometry(2, 1, 0.3);
        break;
      case 'crate':
        geometry = new THREE.BoxGeometry(1.2, 1.2, 1.2);
        color = new THREE.Color('#8b5a2b');
        break;
      case 'car':
        geometry = new THREE.BoxGeometry(1.8, 1.1, 4.2);
        color = new THREE.Color('#4f5d75');
        break;
      case 'debris':
        geometry = new THREE.DodecahedronGeometry(0.8, 0);
        color = new THREE.Color('#666666');
        break;
      default:
        return;
    }

    const material = new THREE.MeshStandardMaterial({
      color,
      metalness: 0.5,
      roughness: 0.6
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...obstacle.position);
    if (obstacle.scale) {
      mesh.scale.setScalar(obstacle.scale);
    }
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.environmentGroup.add(mesh);
  }

  private setupDynamicLighting(env: any): void {
    // Alle Neon-Lichter werden später animiert
    // (Flashing, Pulsing Effekte)
  }

  /**
   * Animation-Loop für Neon-Flashing
   */
  update(deltaTime: number): void {
    this.animatedNeons.forEach((neon, idx) => {
      const pulse = Math.sin(deltaTime * 0.003 + idx) * 0.5 + 0.5;
      const material = neon.mesh.material as THREE.MeshStandardMaterial;
      material.emissiveIntensity = neon.intensity * pulse;

      // Auch Lichter pulsieren lassen
      if (this.neonLights[idx]) {
        this.neonLights[idx].intensity = neon.intensity * pulse * 2;
      }
    });
  }

  getEnvironmentGroup(): THREE.Group {
    return this.environmentGroup;
  }

  getNeonLights(): THREE.PointLight[] {
    return this.neonLights;
  }
  
  /**
   * Zeigt/Versteckt die Umgebung
   */
  setVisible(visible: boolean): void {
    this.environmentGroup.visible = visible;
    if (this.loadedEnvironmentMap) {
      this.loadedEnvironmentMap.visible = visible;
    }
    this.neonLights.forEach(light => {
      light.visible = visible;
    });
  }
}
